import { create, StateCreator } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'job_seeker' | 'employer';
  profile: UserProfile;
  preferences: UserPreferences;
}

export interface UserProfile {
  title: string;
  location: string;
  bio: string;
  skills: string[];
  experience: string;
  education: string;
  linkedin?: string;
  website?: string;
  avatar?: string;
}

export interface UserPreferences {
  notifications: {
    jobAlerts: boolean;
    applicationUpdates: boolean;
    marketing: boolean;
  };
  privacy: {
    profileVisible: boolean;
    allowDirectMessages: boolean;
  };
  search: {
    savedSearches: SavedSearch[];
    jobAlerts: JobAlert[];
  };
}

export interface SavedSearch {
  id: string;
  name: string;
  filters: any;
  createdAt: string;
  lastUsed: string;
}

export interface JobAlert {
  id: string;
  name: string;
  keywords: string;
  location: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  isActive: boolean;
}

interface UserState {
  // User Data
  user: User | null;
  isAuthenticated: boolean;

  // Applications
  applications: Application[];

  // Saved Jobs
  savedJobs: string[]; // Job IDs

  // Notifications
  notifications: Notification[];
  unreadCount: number;

  // Actions
  setUser: (user: User | null) => void;
  updateProfile: (profile: Partial<UserProfile>) => void;
  updatePreferences: (preferences: Partial<UserPreferences>) => void;

  // Application Actions
  addApplication: (application: Application) => void;
  updateApplicationStatus: (applicationId: string, status: string) => void;
  getApplications: () => Application[];

  // Saved Jobs Actions
  toggleSavedJob: (jobId: string) => void;
  isJobSaved: (jobId: string) => boolean;

  // Notification Actions
  addNotification: (notification: Notification) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: string) => void;

  // Search Actions
  saveSearch: (name: string, filters: any) => void;
  deleteSavedSearch: (searchId: string) => void;
  createJobAlert: (alert: Omit<JobAlert, 'id'>) => void;
  updateJobAlert: (alertId: string, updates: Partial<JobAlert>) => void;
  deleteJobAlert: (alertId: string) => void;
}

export interface Application {
  id: string;
  jobId: string;
  jobTitle: string;
  company: string;
  appliedDate: string;
  status: 'pending' | 'accepted' | 'rejected';
  notes?: string;
}

export interface Notification {
  id: string;
  type: 'job_alert' | 'application_update' | 'new_job' | 'system';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  actionUrl?: string;
}

const initialUser: User = {
  id: '',
  email: '',
  firstName: '',
  lastName: '',
  role: 'job_seeker',
  profile: {
    title: '',
    location: '',
    bio: '',
    skills: [],
    experience: '',
    education: '',
  },
  preferences: {
    notifications: {
      jobAlerts: true,
      applicationUpdates: true,
      marketing: false,
    },
    privacy: {
      profileVisible: true,
      allowDirectMessages: false,
    },
    search: {
      savedSearches: [],
      jobAlerts: [],
    },
  },
};

type UserPersist = (
  config: StateCreator<UserState>,
  options: PersistOptions<UserState, Pick<UserState, 'savedJobs' | 'applications'>>
) => StateCreator<UserState>;

const createUserStore: StateCreator<UserState> = (set, get) => ({
  // Initial State
  user: null,
  isAuthenticated: false,
  applications: [],
  savedJobs: [],
  notifications: [],
  unreadCount: 0,

  // User Actions
  setUser: (user: User | null) => set({
    user,
    isAuthenticated: !!user
  }),

  updateProfile: (profile: Partial<UserProfile>) => set((state: UserState) => ({
    user: state.user ? { ...state.user, profile: { ...state.user.profile, ...profile } } : null
  })),

  updatePreferences: (preferences: Partial<UserPreferences>) => set((state: UserState) => ({
    user: state.user ? {
      ...state.user,
      preferences: { ...state.user.preferences, ...preferences }
    } : null
  })),

  // Application Actions
  addApplication: (application: Application) => set((state: UserState) => ({
    applications: [...state.applications, application]
  })),

  updateApplicationStatus: (applicationId: string, status: string) => set((state: UserState) => ({
    applications: state.applications.map((app: Application) =>
      app.id === applicationId ? { ...app, status: status as Application['status'] } : app
    )
  })),

  getApplications: () => get().applications,

  // Saved Jobs Actions
  toggleSavedJob: (jobId: string) => set((state: UserState) => {
    const isSaved = state.savedJobs.includes(jobId);
    return {
      savedJobs: isSaved
        ? state.savedJobs.filter((id: string) => id !== jobId)
        : [...state.savedJobs, jobId]
    };
  }),

  isJobSaved: (jobId: string) => get().savedJobs.includes(jobId),

  // Notification Actions
  addNotification: (notification: Notification) => set((state: UserState) => ({
    notifications: [notification, ...state.notifications],
    unreadCount: state.unreadCount + 1
  })),

  markAsRead: (notificationId: string) => set((state: UserState) => ({
    notifications: state.notifications.map((notif: Notification) =>
      notif.id === notificationId ? { ...notif, read: true } : notif
    ),
    unreadCount: Math.max(0, state.unreadCount - 1)
  })),

  markAllAsRead: () => set((state: UserState) => ({
    notifications: state.notifications.map((notif: Notification) => ({ ...notif, read: true })),
    unreadCount: 0
  })),

  removeNotification: (notificationId: string) => set((state: UserState) => {
    const notification = state.notifications.find((n: Notification) => n.id === notificationId);
    return {
      notifications: state.notifications.filter((n: Notification) => n.id !== notificationId),
      unreadCount: notification && !notification.read
        ? Math.max(0, state.unreadCount - 1)
        : state.unreadCount
    };
  }),

  // Search Actions
  saveSearch: (name: string, filters: any) => set((state: UserState) => {
    if (!state.user) return state;

    const newSearch: SavedSearch = {
      id: Date.now().toString(),
      name,
      filters,
      createdAt: new Date().toISOString(),
      lastUsed: new Date().toISOString(),
    };

    return {
      user: {
        ...state.user,
        preferences: {
          ...state.user.preferences,
          search: {
            ...state.user.preferences.search,
            savedSearches: [...state.user.preferences.search.savedSearches, newSearch]
          }
        }
      }
    };
  }),

  deleteSavedSearch: (searchId: string) => set((state: UserState) => {
    if (!state.user) return state;

    return {
      user: {
        ...state.user,
        preferences: {
          ...state.user.preferences,
          search: {
            ...state.user.preferences.search,
            savedSearches: state.user.preferences.search.savedSearches.filter((s: SavedSearch) => s.id !== searchId)
          }
        }
      }
    };
  }),

  createJobAlert: (alert: Omit<JobAlert, 'id'>) => set((state: UserState) => {
    if (!state.user) return state;

    const newAlert: JobAlert = {
      ...alert,
      id: Date.now().toString(),
    };

    return {
      user: {
        ...state.user,
        preferences: {
          ...state.user.preferences,
          search: {
            ...state.user.preferences.search,
            jobAlerts: [...state.user.preferences.search.jobAlerts, newAlert]
          }
        }
      }
    };
  }),

  updateJobAlert: (alertId: string, updates: Partial<JobAlert>) => set((state: UserState) => {
    if (!state.user) return state;

    return {
      user: {
        ...state.user,
        preferences: {
          ...state.user.preferences,
          search: {
            ...state.user.preferences.search,
            jobAlerts: state.user.preferences.search.jobAlerts.map((alert: JobAlert) =>
              alert.id === alertId ? { ...alert, ...updates } : alert
            )
          }
        }
      }
    };
  }),

  deleteJobAlert: (alertId: string) => set((state: UserState) => {
    if (!state.user) return state;

    return {
      user: {
        ...state.user,
        preferences: {
          ...state.user.preferences,
          search: {
            ...state.user.preferences.search,
            jobAlerts: state.user.preferences.search.jobAlerts.filter((alert: JobAlert) => alert.id !== alertId)
          }
        }
      }
    };
  }),
});

export const useUserStore = create<UserState>()(
  persist(
    createUserStore,
    {
      name: 'user-store',
      partialize: (state: UserState) => ({
        savedJobs: state.savedJobs,
        applications: state.applications,
      }),
    }
  )
);
